import { useMemo, useState } from 'react';
import { Download, FileSpreadsheet, FileText, Plus, TriangleAlert } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button, FormDialog, messageFromError, toast, unwrapAction } from '@eo/shared';

import { requestExcessExport } from './api/api';
import { excessAdditionFormConfig } from './config/formConfig';
import { EXCESS_FORMAT_LABELS, EXCESS_FORMATS } from './constants';
import { ExcessEntries } from './ExcessEntries';
import { ExcessSummary } from './ExcessSummary';
import {
  billCategoryOrder,
  emptyExcessDraft,
  excessLines,
  excessTotals,
  generateExcessBody,
  pendingChangeCount,
  quarterHours,
  validateExcessAddition,
} from './helpers';
import type {
  ExcessAdditionFormContext,
  ExcessAdditionValues,
  ExcessDraft,
  ExcessFormat,
  ExcessHoursProps,
} from './types';

const FORMAT_ICON = { excel: FileSpreadsheet, pdf: FileText } as const;

// The bill is a draft until it is generated: edits, archived entries and added
// entries only reach ClickUp then, and only a generated bill can be downloaded,
// so the file always matches what ClickUp holds.
export function ExcessHours({
  bill,
  month,
  clientTasks,
  onGenerateExcess,
  onQueryOptions,
}: ExcessHoursProps) {
  const [searchParams] = useSearchParams();
  const [draft, setDraft] = useState<ExcessDraft>(() => emptyExcessDraft(bill));
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState<ExcessFormat | null>(null);
  const [adding, setAdding] = useState(false);

  const lines = excessLines(bill, draft);
  const totals = excessTotals(lines, bill, draft.writeoffHours);
  const categoryOrder = useMemo(() => billCategoryOrder(bill), [bill]);
  const pending = pendingChangeCount(bill, draft);

  const onHours = (key: string, hours: number) =>
    setDraft((d) => ({ ...d, hours: { ...d.hours, [key]: hours } }));
  const onToggleArchive = (key: string) =>
    setDraft((d) => {
      if (d.additions.some((a) => a.key === key)) {
        return { ...d, additions: d.additions.filter((a) => a.key !== key) };
      }
      const current = d.archived[key] ?? bill.entries.find((e) => e.entryId === key)?.archived;
      return { ...d, archived: { ...d.archived, [key]: !current } };
    });
  const onAdd = async (values: ExcessAdditionValues) => {
    const addition = {
      key: `new-${crypto.randomUUID()}`,
      taskId: values.taskId,
      taskName: values.taskName,
      date: values.date,
      hours: quarterHours(Number(values.hours)),
      ...(values.note.trim() && { note: values.note.trim() }),
    };
    setDraft((d) => ({ ...d, additions: [...d.additions, addition] }));
  };

  const onGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      unwrapAction(
        await onGenerateExcess({
          body: generateExcessBody(bill, draft),
          month,
          query: Object.fromEntries(searchParams.entries()),
        }),
      );
      toast.success('Bill generated. ClickUp now has every change.');
    } catch (err) {
      toast.error(
        messageFromError(err, 'Generating the bill failed. Nothing was lost — try again.'),
      );
      setGenerating(false);
    }
  };

  // The platform renders the file and emails its link, so only a generated bill
  // it can file is sent.
  const onDownload = async (format: ExcessFormat) => {
    setExporting(format);
    try {
      const { recipientEmail } = await requestExcessExport({
        clientId: bill.clientId,
        month,
        format: format === 'excel' ? 'xlsx' : 'pdf',
      });
      toast.success(
        `The ${EXCESS_FORMAT_LABELS[format]} report is being prepared. We will email ${recipientEmail} a link to download it.`,
      );
    } catch (err) {
      toast.error(messageFromError(err, 'The download failed. Try again.'));
    } finally {
      setExporting(null);
    }
  };

  const blockedReason = (): string | undefined => {
    if (pending > 0) return 'Generate the bill first: it has unsaved changes.';
    if (!bill.generatedAt) return 'Generate the bill first.';
    return undefined;
  };

  const status =
    pending > 0
      ? `${pending} unsaved ${pending === 1 ? 'change' : 'changes'}. Nothing reaches ClickUp until you generate the bill.`
      : bill.generatedAt
        ? `Generated ${new Date(bill.generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${bill.generatedBy ? ` by ${bill.generatedBy}` : ''}.`
        : 'Edited hours, archived entries and added entries reach ClickUp only when you generate the bill.';

  return (
    <section data-testid="excess-hours" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p
          role="status"
          className="flex max-w-xl items-start gap-2 text-xs text-text-tertiary"
          data-testid="excess-status"
        >
          <TriangleAlert
            aria-hidden="true"
            className={
              pending > 0 ? 'mt-px size-4 shrink-0 text-text-primary' : 'mt-px size-4 shrink-0'
            }
          />
          <span className={pending > 0 ? 'text-text-primary' : undefined}>{status}</span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => setAdding(true)}>
            <Plus />
            Add entry
          </Button>
          <Button
            disabled={generating || (pending === 0 && bill.generatedAt !== null)}
            onClick={() => void onGenerate()}
          >
            {generating ? 'Generating…' : 'Generate bill'}
          </Button>
          <span className="mx-1 h-6 w-px bg-border-faint" aria-hidden="true" />
          {EXCESS_FORMATS.map((format) => {
            const Icon = exporting === format ? Download : FORMAT_ICON[format];
            return (
              <Button
                key={format}
                variant="secondary"
                disabled={blockedReason() !== undefined || exporting !== null}
                title={blockedReason()}
                onClick={() => void onDownload(format)}
              >
                <Icon className={exporting === format ? 'animate-pulse' : undefined} />
                {EXCESS_FORMAT_LABELS[format]}
              </Button>
            );
          })}
        </div>
      </div>

      <ExcessEntries
        lines={lines}
        categoryOrder={categoryOrder}
        onHours={onHours}
        onToggleArchive={onToggleArchive}
      />
      <div className="lg:ml-auto lg:w-1/2">
        <ExcessSummary
          totals={totals}
          writeoffHours={draft.writeoffHours}
          onWriteoff={(hours) => setDraft((d) => ({ ...d, writeoffHours: hours }))}
        />
      </div>

      <FormDialog<ExcessAdditionValues, ExcessAdditionFormContext>
        open={adding}
        onOpenChange={setAdding}
        title="Add an entry"
        description={`Logged in ClickUp as variable time on ${bill.clientName}, under your name, when you generate the bill.`}
        config={excessAdditionFormConfig}
        context={{ clientId: bill.clientId, clientTasks, onQueryOptions }}
        initial={{ taskId: '', taskName: '', date: `${month.slice(0, 8)}01`, hours: '1', note: '' }}
        validate={validateExcessAddition(month)}
        onSubmit={onAdd}
        submitLabel="Add to bill"
        pendingLabel="Adding…"
      />
    </section>
  );
}
